/**
 * Custom hook for the quarterly revenue target snapshot and config
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type {
  RevenueTargetSnapshot,
  RevenueTargetConfig,
  RevenueTargetConfigInput,
} from '@/types/revenueTarget';

interface UseRevenueTargetResult {
  data: RevenueTargetSnapshot | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  /** Selected quarter end month (YYYY-MM); null = current quarter */
  quarter: string | null;
  selectQuarter: (quarterKey: string | null) => void;
  refresh: () => Promise<void>;
  saveConfig: (input: RevenueTargetConfigInput) => Promise<boolean>;
}

export function useRevenueTarget(): UseRevenueTargetResult {
  const [data, setData] = useState<RevenueTargetSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [quarter, setQuarter] = useState<string | null>(null);

  // The selected quarter is mirrored in a ref so fetchData can read it
  // without taking it as a dependency — that keeps a save able to reset
  // the selection and fetch the right quarter in the same tick.
  const quarterRef = useRef<string | null>(null);
  // Only the newest request may write state: switching quarters quickly
  // must not let a slow earlier response overwrite the current one.
  const requestSeq = useRef(0);

  const fetchData = useCallback(async (refresh = false) => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (refresh) params.set('refresh', 'true');
      if (quarterRef.current) params.set('quarter', quarterRef.current);
      const query = params.toString();
      const url = query ? `/revenue-target?${query}` : '/revenue-target';
      const response = await apiClient.get<{ data: RevenueTargetSnapshot }>(url);
      if (seq !== requestSeq.current) return;
      setData(response.data.data);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      try {
        handleApiError(err, 'Failed to load revenue target');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load data'
        );
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, quarter]);

  const refresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  /**
   * Move to another quarter. The old snapshot is dropped first so a
   * quarter that cannot be loaded shows the error screen instead of
   * leaving the previous quarter's figures under the new selection.
   */
  const selectQuarter = useCallback((quarterKey: string | null) => {
    if (quarterRef.current === quarterKey) return;
    quarterRef.current = quarterKey;
    setData(null);
    setError(null);
    setQuarter(quarterKey);
  }, []);

  const saveConfig = useCallback(
    async (input: RevenueTargetConfigInput): Promise<boolean> => {
      try {
        setSaving(true);
        setSaveError(null);
        await apiClient.put<{ data: RevenueTargetConfig }>(
          '/revenue-target/config',
          input
        );
        // Changing the financial year start redefines the quarters, so a
        // selected quarter key can stop being a valid quarter end. Always
        // return to the current quarter after a save. When that is a real
        // change the effect above refetches; otherwise fetch here.
        const wasCurrentQuarter = quarterRef.current === null;
        quarterRef.current = null;
        setQuarter(null);
        if (wasCurrentQuarter) {
          await fetchData(true);
        }
        return true;
      } catch (err) {
        try {
          handleApiError(err, 'Failed to save target settings');
        } catch (handled) {
          setSaveError(
            handled instanceof Error ? handled.message : 'Failed to save'
          );
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchData]
  );

  return {
    data,
    loading,
    error,
    saving,
    saveError,
    quarter,
    selectQuarter,
    refresh,
    saveConfig,
  };
}
